import { useCallback, useState } from 'react'
import { nanoid } from 'nanoid'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { useTranslation } from '../../contexts/LanguageContext'
import type { PersonalInfo, Link } from '../../types/cv'

interface LinksFormProps {
  data: PersonalInfo | undefined
  onChange: (data: Partial<PersonalInfo>) => void
}

export function LinksForm({ data, onChange }: LinksFormProps) {
  const { t } = useTranslation()
  const [linkError, setLinkError] = useState<string>('')

  const addLink = useCallback(() => {
    const newLink: Link = { id: nanoid(), label: '', url: '' }
    onChange({ links: [...(data?.links || []), newLink] })
  }, [data?.links, onChange])

  const updateLink = useCallback(
    (id: string, field: keyof Link, value: string) => {
      const links = (data?.links || []).map((link) =>
        link.id === id ? { ...link, [field]: value } : link
      )
      onChange({ links })
      setLinkError('')
    },
    [data?.links, onChange]
  )

  const removeLink = useCallback(
    (id: string) => {
      const links = (data?.links || []).filter((link) => link.id !== id)
      onChange({ links })
    },
    [data?.links, onChange]
  )

  const validateUrl = (url: string): boolean => {
    if (!url) return true
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`)
      return true
    } catch {
      return false
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-600">
          {t.linksForm.empty}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addLink}
        >
          {t.linksForm.add}
        </Button>
      </div>

      {(data?.links || []).length === 0 && (
        <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <svg
            className="w-8 h-8 mx-auto text-slate-400 mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
            />
          </svg>
          <p className="text-sm text-slate-500">{t.linksForm.noLinks}</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={addLink}
            className="mt-2"
          >
            {t.linksForm.addFirst}
          </Button>
        </div>
      )}

      <div className="space-y-3">
        {(data?.links || []).map((link) => (
          <div key={link.id} className="flex gap-2 items-start">
            <div className="w-28 shrink-0">
              <Input
                placeholder={t.linksForm.labelPlaceholder}
                value={link.label}
                onChange={(e) => updateLink(link.id, 'label', e.target.value)}
              />
            </div>
            <div className="flex-1 min-w-0">
              <Input
                placeholder={t.linksForm.urlPlaceholder}
                value={link.url}
                onChange={(e) => {
                  const url = e.target.value
                  updateLink(link.id, 'url', url)
                  if (url && !validateUrl(url)) {
                    setLinkError(t.linksForm.invalidUrl)
                  }
                }}
                error={link.url && !validateUrl(link.url) ? linkError : undefined}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeLink(link.id)}
              className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0 mt-1"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}
